import { useTranslation } from 'react-i18next';
import { useIsObserver } from '@platform/features/auth';
import { BaElsewhereNote } from '../../insights/BaElsewhereNote';
import { AdvancedChips } from '../advanced/AdvancedChips';
import { AdvancedPanel } from '../advanced/AdvancedPanel';
import { useAdvancedDsl } from '../advanced/useAdvancedDsl';
import { useAdvancedPanel } from '../advanced/useAdvancedPanel';
import { hasActiveFilters, RESET_FILTERS } from '../filters/activeFilters';
import { FilterRow } from '../filters/FilterRow';
import { useSaveAsView } from '../savedViews/useSaveAsView';
import { CreateKindMenu } from '../toolbar/CreateKindMenu';
import { ListToolbar } from '../toolbar/ListToolbar';
import { TableActions } from '../toolbar/TableActions';
import { useCanExport } from '../toolbar/useCanExport';
import { useCapability } from '../toolbar/useCapability';
import type { IRecordListHeadProps } from './IRecordListHeadProps';
import { PageHeader } from './PageHeader';

/**
 * Шапка сторінки таблиці модуля (прототип v4): заголовок «Модуль — Таблиця» з описом; рядок фільтрів (вкладки подань, чипи пункту меню,
 * швидкого фільтра й умов, «Фільтр», «Розширені фільтри», збережене подання); панель розширених фільтрів; у бізнес-аналізі простору без
 * власних записів — примітка «аналіз платформи — в іншому просторі» (BaElsewhereNote, прототип baElsewhere); панель дій зі створенням (типу
 * з кількома видами записів — меню «+ Створити ▾»), діями над вибраними, експортом, друком, скиданням фільтрів, визначенням модуля, тегами
 * й пошуком. Дії зі змінами — за можливостями простору (`edit`, `delete`); у сесії спостерігача — без «Зберегти як подання».
 */
export function RecordListHead({ model, title, eyebrow, description, actions, createText, createKinds, item, views }: IRecordListHeadProps) {
  const { t } = useTranslation();
  const adv = useAdvancedPanel(model);
  const { clearAll, close } = adv;
  const saveAsView = useSaveAsView(model, useAdvancedDsl(model.ws, model.rt.key) ?? '', () => { clearAll(); close(); });
  const canExport = useCanExport(model.ws);
  const canCreate = useCapability(model.ws, 'edit');
  const canDelete = useCapability(model.ws, 'delete');
  const observer = useIsObserver();
  return (
    <>
      <PageHeader eyebrow={eyebrow} title={title} description={description} />
      <FilterRow model={model} wholeType table item={item} views={views} advanced={{ count: adv.applied, open: adv.open, onToggle: adv.toggle }}
        chips={<AdvancedChips ws={model.ws} rt={model.rt} conditions={adv.conditions} onChange={adv.change} quick={adv.quick} onQuick={adv.changeQuick} />} />
      {adv.open && (
        <AdvancedPanel model={model} conditions={adv.conditions} onChange={adv.change} quick={adv.quick} onQuick={adv.changeQuick}
          matches={model.page?.total} onClose={close} onSaveAsView={observer ? undefined : saveAsView} />
      )}
      <BaElsewhereNote ws={model.ws} moduleKey={model.module.module.key} rtKey={model.rt.key} />
      <ListToolbar model={model}>
        {canCreate && createKinds && <CreateKindMenu label={createText} menuLabel={t('listbar.createKindMenu')} items={createKinds} />}
        <TableActions actions={actions} createText={createText} selected={model.selection.length} canExport={canExport} canCreate={canCreate && !createKinds}
          canEdit={canCreate} canDelete={canDelete} filtersActive={hasActiveFilters(model.state)} onReset={() => model.setState(RESET_FILTERS)} />
      </ListToolbar>
    </>
  );
}
